import { getStripeAppearance, stripeFonts } from "@/modules/stripe/getStripeAppearance";
import { Elements, PaymentElement, useElements, useStripe } from "@stripe/react-stripe-js";
import { type FormEvent, useState } from "react";
import { type LoaderFunction, redirect, useLoaderData, useNavigate } from "react-router-dom";

import {
  getPlatformUserMemberships,
  postPlatformUserMembershipsByPlatformUserMembershipIdCreateStripeUpdateSession,
} from "api/methods";
import type { Models } from "api/types";
import { useTranslation } from "@/core/i18n";
import { Button } from "atoms/button/Button";
import ChevronLeft from "atoms/icon/icons/chevron_left.svg";
import TopNav from "molecules/navbars/TopNav";
import { stripeLoader, useStripePublishableKey } from "modules/stripe";
import { queryClient } from "pages/Root";
import { PAYMENT_UPDATED_PARAM, UPDATED_MEMBERSHIP_ID_PARAM } from "constants/index";

import { invalidateMembershipQueries } from "./membershipHelper";

const load = async (platformUserMembershipId: string) => {
  const memberships = await queryClient.fetchQuery({ ...getPlatformUserMemberships.keyed() });
  const membership = memberships.find(
    (item) => item.platformUserMembershipId === platformUserMembershipId,
  );

  if (membership?.canceled || !membership?.paymentProcessorSubscriptionId?.trim()) {
    return null;
  }

  const intent =
    await postPlatformUserMembershipsByPlatformUserMembershipIdCreateStripeUpdateSession({
      platformUserMembershipId,
    });

  return { membership, intent };
};

type LoaderData = NonNullable<Awaited<ReturnType<typeof load>>>;

export const walletMembershipPaymentMethodLoader: LoaderFunction = async ({
  params: { platformUserMembershipId },
}) => {
  if (!platformUserMembershipId) {
    return redirect("/wallet/memberships/manage");
  }

  const data = await load(platformUserMembershipId);
  return data ?? redirect("/wallet/memberships/manage");
};

const MembershipPaymentMethodForm = ({
  membership,
}: {
  membership: Models<"PlatformUserMembershipModel">;
}) => {
  const { t } = useTranslation();
  const stripe = useStripe();
  const elements = useElements();
  const navigate = useNavigate();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string>();

  const returnToMemberships = () => {
    // The manage page loader serves the memberships list from cache while it is fresh, and the
    // copy fetched to open this form predates the card that was just confirmed.
    void invalidateMembershipQueries(queryClient);
    const search = new URLSearchParams({
      [PAYMENT_UPDATED_PARAM]: "1",
      [UPDATED_MEMBERSHIP_ID_PARAM]: membership.platformUserMembershipId,
    });
    navigate(`/wallet/memberships/manage?${search.toString()}`);
  };

  const submit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!stripe || !elements || isSubmitting) {
      return;
    }

    setIsSubmitting(true);
    setErrorMessage(undefined);

    const returnUrl = new URL("/wallet/memberships/manage", globalThis.location.origin);
    returnUrl.searchParams.set(PAYMENT_UPDATED_PARAM, "1");
    returnUrl.searchParams.set(UPDATED_MEMBERSHIP_ID_PARAM, membership.platformUserMembershipId);

    const { error } = await stripe.confirmSetup({
      elements,
      confirmParams: { return_url: returnUrl.toString() },
      redirect: "if_required",
    });

    if (error) {
      setErrorMessage(error.message || t("walletManageMembershipsPage.paymentMethodUpdateFailed"));
      setIsSubmitting(false);
      return;
    }

    returnToMemberships();
  };

  return (
    <form className="flex flex-col gap-y-6" onSubmit={submit}>
      <div data-testid="embedded-membership-payment-method" className="rounded-lg bg-white">
        <PaymentElement options={{ layout: "tabs" }} />
      </div>
      {errorMessage && (
        <div role="alert" className="rounded-lg bg-red/10 px-4 py-3 text-body2 text-red">
          {errorMessage}
        </div>
      )}
      <Button fullWidth submit disabled={!stripe || !elements || isSubmitting}>
        {isSubmitting ? t("common.loading") : t("common.saveChanges")}
      </Button>
    </form>
  );
};

export const WalletMembershipPaymentMethodPage = () => {
  const { t } = useTranslation();
  const { membership, intent } = useLoaderData() as LoaderData;
  const navigate = useNavigate();
  const publishableKey = useStripePublishableKey();

  return (
    <div className="relative flex min-h-full flex-col bg-white">
      <TopNav
        startIconSVG={ChevronLeft}
        showHeaderItems={false}
        onStartIconClick={() => navigate(-1)}
      >
        <div className="flex justify-center text-sub2">
          {t("walletMembershipPage.updatePaymentMethod").toUpperCase()}
        </div>
      </TopNav>
      <main className="mx-auto flex w-full max-w-xl grow flex-col gap-y-6 px-6 py-8 text-left">
        <div className="flex flex-col gap-y-2">
          <h1 className="font-serif text-h2 text-primary">
            {t("walletMembershipPage.updatePaymentMethod")}
          </h1>
          <p className="text-body1 text-secondary">{membership.membership.name}</p>
        </div>
        {publishableKey ? (
          <Elements
            stripe={stripeLoader.getStripe(publishableKey)}
            options={{
              clientSecret: intent.clientSecret,
              appearance: getStripeAppearance(),
              fonts: stripeFonts,
            }}
          >
            <MembershipPaymentMethodForm membership={membership} />
          </Elements>
        ) : (
          <div className="flex grow items-center justify-center text-body1 text-secondary">
            {t("common.loading")}
          </div>
        )}
      </main>
    </div>
  );
};
